/**
 * Customer API Client
 * 고객 관리 API 호출 (POWER/ADMIN 전용)
 */

import apiClient from "./client";
import type {
  Customer,
  CustomerSearchParams,
  PagedCustomers,
  CustomerFormData,
  CustomerStats,
} from "@/types/customer";
import type { PagedConsults } from "@/types/consult";
import type {
  PagedAppointments,
  CustomerBookingStats,
} from "@/types/appointment";
import { PAGINATION } from "@/constants/pagination";
import { SORT_ORDER } from "@/constants/sort";

export const customerApi = {
  /**
   * 고객 목록 조회 (검색/페이징)
   * GET /api/customers
   */
  getCustomers: async (
    params: CustomerSearchParams,
  ): Promise<PagedCustomers> => {
    const queryParams = new URLSearchParams();

    if (params.q) {
      queryParams.append("q", params.q);
    }
    if (params.starred !== undefined) {
      queryParams.append("starred", params.starred.toString());
    }
    if (params.page !== undefined) {
      queryParams.append("page", params.page.toString());
    }
    if (params.size !== undefined) {
      queryParams.append("size", params.size.toString());
    }
    if (params.sort) {
      queryParams.append("sort", params.sort);
    }
    if (params.segment) {
      queryParams.append("segment", params.segment);
    }

    const response = await apiClient.get<PagedCustomers>(
      `/api/customers?${queryParams.toString()}`,
    );
    return response.data;
  },

  /**
   * 고객 요약 통계 (필터 무관, 전체 기준)
   * GET /api/customers/summary-stats
   */
  getSummaryStats: async (): Promise<{
    total: number;
    starred: number;
    newThisMonth: number;
    dormant: number;
  }> => {
    const response = await apiClient.get<{
      total: number;
      starred: number;
      newThisMonth: number;
      dormant: number;
    }>("/api/customers/summary-stats");
    return response.data;
  },

  /**
   * 고객 생성
   * POST /api/customers
   */
  createCustomer: async (data: CustomerFormData): Promise<Customer> => {
    const response = await apiClient.post<Customer>("/api/customers", data);
    return response.data;
  },

  /**
   * 고객 수정
   * PUT /api/customers/{id}
   */
  updateCustomer: async (
    id: number,
    data: CustomerFormData,
  ): Promise<Customer> => {
    const response = await apiClient.put<Customer>(
      `/api/customers/${id}`,
      data,
    );
    return response.data;
  },

  /**
   * 고객 상세 조회
   * GET /api/customers/{id}
   */
  getCustomer: async (id: number): Promise<Customer> => {
    const response = await apiClient.get<Customer>(`/api/customers/${id}`);
    return response.data;
  },

  /**
   * 고객 삭제
   * DELETE /api/customers/{id}
   */
  deleteCustomer: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/customers/${id}`);
  },

  /**
   * 고객 통계 조회
   * GET /api/customers/{id}/stats
   */
  getCustomerStats: async (id: number): Promise<CustomerStats> => {
    const response = await apiClient.get<CustomerStats>(
      `/api/customers/${id}/stats`,
    );
    return response.data;
  },

  /**
   * 고객 예약 통계 조회 (방문/노쇼/취소 횟수)
   * GET /api/customers/{id}/booking-stats
   */
  getCustomerBookingStats: async (
    id: number,
  ): Promise<CustomerBookingStats> => {
    const response = await apiClient.get<CustomerBookingStats>(
      `/api/customers/${id}/booking-stats`,
    );
    return response.data;
  },

  /**
   * 고객 즐겨찾기 토글
   * PUT /api/customers/{id}/star
   */
  toggleStar: async (id: number): Promise<Customer> => {
    const response = await apiClient.put<Customer>(`/api/customers/${id}/star`);
    return response.data;
  },

  /**
   * 고객별 상담 이력 조회 (페이징)
   * GET /api/customers/{id}/consults
   * @param unassignedOnly true이면 미배분 상담만 조회
   */
  getCustomerConsults: async (
    id: number,
    page = 0,
    size: number = PAGINATION.SMALL_SIZE,
    unassignedOnly = false,
  ): Promise<PagedConsults> => {
    const params = new URLSearchParams({
      page: String(page),
      size: String(size),
      sort: SORT_ORDER.CONSULT,
    });
    if (unassignedOnly) {
      params.set("unassignedOnly", "true");
    }
    const response = await apiClient.get<PagedConsults>(
      `/api/customers/${id}/consults?${params.toString()}`,
    );
    return response.data;
  },

  /**
   * 고객별 예약 이력 조회 (페이징)
   * GET /api/customers/{id}/appointments
   */
  getCustomerAppointments: async (
    id: number,
    page = 0,
    size: number = PAGINATION.SMALL_SIZE,
  ): Promise<PagedAppointments> => {
    const response = await apiClient.get<PagedAppointments>(
      `/api/customers/${id}/appointments?page=${page}&size=${size}&sort=${SORT_ORDER.APPOINTMENT}`,
    );
    return response.data;
  },

  /**
   * V175: 고객 일괄 생성 (엑셀 업로드) (#2, #3)
   * POST /api/customers/bulk
   */
  bulkCreate: async (
    customers: CustomerFormData[],
  ): Promise<{ success: number; failed: number; errors: string[] }> => {
    const response = await apiClient.post<{
      success: number;
      failed: number;
      errors: string[];
    }>("/api/customers/bulk", { customers });
    return response.data;
  },

  /**
   * V1096: 고객 예약 차단 토글
   * PUT /api/customers/{id}/block
   */
  toggleBlock: async (
    customerId: number,
    blocked: boolean,
    reason?: string,
  ): Promise<void> => {
    await apiClient.put(`/api/customers/${customerId}/block`, {
      blocked,
      reason,
    });
  },
};
